'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { tourMissingMessageKey, type TourMissingReason } from '@/lib/help/nav-tour-prepare';
type Props = {
  stepIndex: number;
  stepCount: number;
  title: string;
  body: string;
  targetRect: DOMRect | null;
  missingAnchor: boolean;
  missingReason?: TourMissingReason;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
};
const TOOLTIP_W = 352;
const TOOLTIP_H = 220;
const GAP = 12;
const MARGIN = 12;
type Pt = {
  top: number;
  left: number;
};
type Hole = {
  top: number;
  left: number;
  width: number;
  height: number;
};
function clampTooltipPosition(preferred: Pt, vw: number, vh: number): Pt {
  return {
    top: Math.min(Math.max(MARGIN, preferred.top), Math.max(MARGIN, vh - TOOLTIP_H - MARGIN)),
    left: Math.min(Math.max(MARGIN, preferred.left), Math.max(MARGIN, vw - TOOLTIP_W - MARGIN))
  };
}
function overlapArea(a: Hole, b: Hole): number {
  const left = Math.max(a.left, b.left);
  const right = Math.min(a.left + a.width, b.left + b.width);
  const top = Math.max(a.top, b.top);
  const bottom = Math.min(a.top + a.height, b.top + b.height);
  if (right <= left || bottom <= top) return 0;
  return (right - left) * (bottom - top);
}

/** Place the tip outside the spotlight whenever possible. */
function placeTooltip(hole: Hole, vw: number, vh: number): Pt {
  const candidates: Pt[] = [
  // Prefer outside the hole — above / below / right / left
  {
    top: hole.top - TOOLTIP_H - GAP,
    left: hole.left
  }, {
    top: hole.top - TOOLTIP_H - GAP,
    left: hole.left + hole.width - TOOLTIP_W
  }, {
    top: hole.top + hole.height + GAP,
    left: hole.left
  }, {
    top: hole.top + hole.height + GAP,
    left: hole.left + hole.width - TOOLTIP_W
  }, {
    top: hole.top,
    left: hole.left + hole.width + GAP
  }, {
    top: hole.top + Math.max(0, hole.height - TOOLTIP_H),
    left: hole.left + hole.width + GAP
  }, {
    top: hole.top,
    left: hole.left - TOOLTIP_W - GAP
  }, {
    top: hole.top + Math.max(0, hole.height - TOOLTIP_H),
    left: hole.left - TOOLTIP_W - GAP
  },
  // Large tables: dock into dimmed chrome corners (never inside the hole if avoidable)
  {
    top: MARGIN,
    left: vw - TOOLTIP_W - MARGIN
  }, {
    top: MARGIN,
    left: MARGIN
  }, {
    top: vh - TOOLTIP_H - MARGIN,
    left: MARGIN
  }, {
    top: vh - TOOLTIP_H - MARGIN,
    left: vw - TOOLTIP_W - MARGIN
  }];
  let best: Pt | null = null;
  let bestOverlap = Number.POSITIVE_INFINITY;
  for (const raw of candidates) {
    const pos = clampTooltipPosition(raw, vw, vh);
    const tip: Hole = {
      top: pos.top,
      left: pos.left,
      width: TOOLTIP_W,
      height: TOOLTIP_H
    };
    const area = overlapArea(tip, hole);
    if (area === 0) {
      return pos;
    }
    if (area < bestOverlap) {
      bestOverlap = area;
      best = pos;
    }
  }
  return best ?? clampTooltipPosition({
    top: MARGIN,
    left: vw - TOOLTIP_W - MARGIN
  }, vw, vh);
}
export function TourOverlay(t0) {
  const $ = _c(88);
  const {
    stepIndex,
    stepCount,
    title,
    body,
    targetRect,
    missingAnchor,
    missingReason: t1,
    onNext,
    onPrev,
    onClose
  } = t0;
  const missingReason = t1 === undefined ? "generic" : t1;
  const {
    t
  } = useTranslation("help");
  let t2;
  let t3;
  if ($[0] !== onClose) {
    t2 = () => {
      const onKey = e => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    };
    t3 = [onClose];
    $[0] = onClose;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  if (typeof document === "undefined") {
    return null;
  }
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  let hole;
  let t4;
  if ($[3] !== targetRect) {
    hole = targetRect ? (() => {
      const top = Math.max(0, targetRect.top - 6);
      const left = Math.max(0, targetRect.left - 6);
      let width = targetRect.width + 12;
      const height = targetRect.height + 12;
      const maxW = Math.min(vw - 24, vw * 0.92);
      const maxH = Math.min(vh - 24, vh * 0.7);
      if (width > maxW) {
        const overflow = width - maxW;
        width = maxW;
        const shifted = left + overflow / 2;
        return {
          top,
          left: Math.min(Math.max(0, shifted), Math.max(0, vw - width)),
          width,
          height: Math.min(height, maxH)
        };
      }
      return {
        top,
        left,
        width,
        height: Math.min(height, maxH)
      };
    })() : null;
    t4 = hole ? placeTooltip(hole, vw, vh) : clampTooltipPosition({
      top: vh * 0.3,
      left: (vw - TOOLTIP_W) / 2
    }, vw, vh);
    $[3] = targetRect;
    $[4] = hole;
    $[5] = t4;
  } else {
    hole = $[4];
    t4 = $[5];
  }
  const tooltipPos = t4;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[6] !== body || $[7] !== hole || $[8] !== missingAnchor || $[9] !== missingReason || $[10] !== onClose || $[11] !== stepCount || $[12] !== stepIndex || $[13] !== t || $[14] !== title || $[15] !== tooltipPos.left || $[16] !== tooltipPos.top) {
    const missingKey = tourMissingMessageKey(missingReason);
    t13 = "fixed inset-0 z-[9999]";
    t14 = "dialog";
    t15 = "true";
    t16 = title;
    if ($[30] !== hole || $[31] !== onClose) {
      t17 = hole ? <><div className="absolute bg-black/50" style={{
          top: 0,
          left: 0,
          right: 0,
          height: hole.top
        }} onClick={onClose} /><div className="absolute bg-black/50" style={{
          top: hole.top + hole.height,
          left: 0,
          right: 0,
          bottom: 0
        }} onClick={onClose} /><div className="absolute bg-black/50" style={{
          top: hole.top,
          left: 0,
          width: hole.left,
          height: hole.height
        }} onClick={onClose} /><div className="absolute bg-black/50" style={{
          top: hole.top,
          left: hole.left + hole.width,
          right: 0,
          height: hole.height
        }} onClick={onClose} /><div className="pointer-events-none absolute rounded-xl ring-2 ring-primary" style={{
          top: hole.top,
          left: hole.left,
          width: hole.width,
          height: hole.height
        }} /></> : <div className="absolute inset-0 bg-black/50" onClick={onClose} />;
      $[30] = hole;
      $[31] = onClose;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = cn("pointer-events-auto absolute z-[10000] w-[min(22rem,calc(100vw-1.5rem))] rounded-xl border border-border bg-card p-4 text-card-foreground shadow-lg");
      $[33] = t5;
    } else {
      t5 = $[33];
    }
    if ($[34] !== tooltipPos.left || $[35] !== tooltipPos.top) {
      t6 = {
        top: tooltipPos.top,
        left: tooltipPos.left
      };
      $[34] = tooltipPos.left;
      $[35] = tooltipPos.top;
      $[36] = t6;
    } else {
      t6 = $[36];
    }
    t7 = _temp;
    t8 = _temp2;
    const t18 = stepIndex + 1;
    let t19;
    if ($[37] !== stepCount || $[38] !== t || $[39] !== t18) {
      t19 = t("tourProgress", {
        current: t18,
        total: stepCount
      });
      $[37] = stepCount;
      $[38] = t;
      $[39] = t18;
      $[40] = t19;
    } else {
      t19 = $[40];
    }
    if ($[41] !== t19) {
      t9 = <p className="text-xs font-medium text-muted-foreground">{t19}</p>;
      $[41] = t19;
      $[42] = t9;
    } else {
      t9 = $[42];
    }
    if ($[43] !== title) {
      t10 = <h2 className="mt-1 text-base font-semibold tracking-tight text-foreground">{title}</h2>;
      $[43] = title;
      $[44] = t10;
    } else {
      t10 = $[44];
    }
    if ($[45] !== body) {
      t11 = <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>;
      $[45] = body;
      $[46] = t11;
    } else {
      t11 = $[46];
    }
    t12 = missingAnchor ? <p className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-sm text-amber-900 dark:text-amber-200">{t(missingKey)}</p> : null;
    $[6] = body;
    $[7] = hole;
    $[8] = missingAnchor;
    $[9] = missingReason;
    $[10] = onClose;
    $[11] = stepCount;
    $[12] = stepIndex;
    $[13] = t;
    $[14] = title;
    $[15] = tooltipPos.left;
    $[16] = tooltipPos.top;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
    $[23] = t16;
    $[24] = t17;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t10 = $[17];
    t11 = $[18];
    t12 = $[19];
    t13 = $[20];
    t14 = $[21];
    t15 = $[22];
    t16 = $[23];
    t17 = $[24];
    t5 = $[25];
    t6 = $[26];
    t7 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t18;
  if ($[47] !== t) {
    t18 = t("tourSkip");
    $[47] = t;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== onClose || $[50] !== t18) {
    t19 = <Button type="button" variant="ghost" size="sm" onClick={onClose}>{t18}</Button>;
    $[49] = onClose;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  const t20 = stepIndex === 0;
  let t21;
  if ($[52] !== t) {
    t21 = t("tourBack");
    $[52] = t;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== onPrev || $[55] !== t20 || $[56] !== t21) {
    t22 = <Button type="button" variant="outline" size="sm" disabled={t20} onClick={onPrev}>{t21}</Button>;
    $[54] = onPrev;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== stepCount || $[59] !== stepIndex || $[60] !== t) {
    t23 = stepIndex + 1 >= stepCount ? t("tourDone") : t("tourNext");
    $[58] = stepCount;
    $[59] = stepIndex;
    $[60] = t;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== onNext || $[63] !== t23) {
    t24 = <Button type="button" size="sm" onClick={onNext}>{t23}</Button>;
    $[62] = onNext;
    $[63] = t23;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== t22 || $[66] !== t24) {
    t25 = <div className="flex gap-2">{t22}{t24}</div>;
    $[65] = t22;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== t19 || $[69] !== t25) {
    t26 = <div className="mt-4 flex flex-wrap items-center justify-between gap-2">{t19}{t25}</div>;
    $[68] = t19;
    $[69] = t25;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== t10 || $[72] !== t11 || $[73] !== t12 || $[74] !== t26 || $[75] !== t5 || $[76] !== t6 || $[77] !== t7 || $[78] !== t8 || $[79] !== t9) {
    t27 = <div className={t5} style={t6} onPointerDown={t7} onClick={t8}>{t9}{t10}{t11}{t12}{t26}</div>;
    $[71] = t10;
    $[72] = t11;
    $[73] = t12;
    $[74] = t26;
    $[75] = t5;
    $[76] = t6;
    $[77] = t7;
    $[78] = t8;
    $[79] = t9;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  let t28;
  if ($[81] !== t13 || $[82] !== t14 || $[83] !== t15 || $[84] !== t16 || $[85] !== t17 || $[86] !== t27) {
    const overlay = <div className={t13} role={t14} aria-modal={t15} aria-label={t16}>{t17}{t27}</div>;
    t28 = createPortal(overlay, document.body);
    $[81] = t13;
    $[82] = t14;
    $[83] = t15;
    $[84] = t16;
    $[85] = t17;
    $[86] = t27;
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  return t28;
}
function _temp2(e_1) {
  return e_1.stopPropagation();
}
function _temp(e_0) {
  return e_0.stopPropagation();
}